<script lang="ts" setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useObjectStore } from '~/stores/objectStore'

const isMenuVisible = ref<boolean>(false)
const isChecksMenuOpen = ref<boolean>(false)
const checksMenuRef = ref<HTMLElement | null>(null)
const route = useRoute()
const envMetadata = inject<{ testing: boolean }>('envMetadata')
const objectStore = useObjectStore()

// Forward the current object's adrcd when switching checks so the context is kept; if no object is
// loaded yet, fall back to the address search.
const currentAdrcd = computed<string | null>(
	() => objectStore.objectAddress?.ADRCD ?? (typeof route.query.adrcd === 'string' ? route.query.adrcd : null)
)
const checkLink = (name: 'object' | 'pv-check' | 'sanierungs-check') =>
	currentAdrcd.value ? { name, query: { adrcd: currentAdrcd.value } } : { name: 'address' }

const initTrapFocus = (e: KeyboardEvent) => {
	return trapFocus(e, 'menu')
}

const openModal = () => {
	isMenuVisible.value = true
	document.addEventListener('keydown', initTrapFocus)
}

const closeModal = () => {
	isMenuVisible.value = false
	document.removeEventListener('keydown', initTrapFocus)
}

const toggleChecksMenu = () => {
	isChecksMenuOpen.value = !isChecksMenuOpen.value
}

const closeChecksMenu = () => {
	isChecksMenuOpen.value = false
}

const isChecksActive = () => {
	return route.name === 'pv-check' || route.name === 'sanierungs-check'
}

const onDocumentClick = (event: MouseEvent) => {
	if (!checksMenuRef.value) return
	if (!checksMenuRef.value.contains(event.target as Node)) {
		closeChecksMenu()
	}
}

const isObjectViewPath = (isActive: boolean) => {
	return route.name === 'object'
}

onBeforeUnmount(() => {
	closeModal()
	document.removeEventListener('click', onDocumentClick)
})

onMounted(() => {
	document.addEventListener('click', onDocumentClick)
})

watch(route, () => {
	closeModal()
	closeChecksMenu()
})
</script>

<template>
	<header
		class="mb-4 flex items-center justify-between border-b border-b-[--schwarz-15] bg-white px-5 pb-5 pt-4 md:mb-0 md:px-0"
	>
		<RouterLink :to="{ name: 'index' }">
			<img
				alt="Land Salzburg Logo"
				src="~/assets/img/landsbg2015_4c_72dpi.png"
				class="h-11 md:h-16 lg:h-20 xl:h-24"
			/>
		</RouterLink>

		<div class="flex gap-1 md:hidden">
			<button aria-label="Navigationsmenü öffnen" aria-controls="menu" class="flex gap-3" @click="openModal">
				<img src="~/assets/img/icons/hamburger-menu.svg" alt="Navigationsmenü öffnen" />
			</button>

			<div
				v-if="isMenuVisible"
				id="menu"
				class="fixed inset-0 z-50 h-screen w-full bg-white"
				aria-label="Navigationsmenü"
				role="dialog"
				tabindex="-1"
				aria-modal="true"
				@keydown.esc="closeModal"
			>
				<div
					class="flex items-center justify-between border-b border-b-[--schwarz-15] px-5 pb-5 pt-4 md:px-0 md:pb-6 lg:pb-8 xl:pb-9"
				>
					<RouterLink :to="{ name: 'index' }">
						<img
							alt="Land Salzburg Logo"
							src="~/assets/img/landsbg2015_4c_72dpi.png"
							class="h-11 md:h-16 lg:h-20 xl:h-24"
						/>
					</RouterLink>

					<button class="flex gap-3" aria-controls="menu" @click="closeModal">
						<img src="~/assets/img/icons/close-menu.svg" alt="Navigationsmenü schließen" />
					</button>
				</div>

				<p class="bg-[--rot] px-4 py-4 text-xl font-bold uppercase text-white md:text-lg">EnergieKompass</p>

				<nav class="flex flex-col">
					<RouterLink
						:to="{ name: 'index' }"
						class="flex justify-between border-b border-b-[--schwarz-15] px-4 py-4 font-bold uppercase"
					>
						<span>Startseite</span>
						<img src="~/assets/img/icons/tabler-icon-menu-item.svg" alt="" />
					</RouterLink>

					<RouterLink
						:to="checkLink('object')"
						class="flex justify-between border-b border-b-[--schwarz-15] px-4 py-4 font-bold uppercase"
					>
						<span>Heizungs-Check</span>
						<img src="~/assets/img/icons/tabler-icon-menu-item.svg" alt="" />
					</RouterLink>

					<RouterLink
						:to="checkLink('pv-check')"
						class="flex justify-between border-b border-b-[--schwarz-15] px-4 py-4 font-bold uppercase"
					>
						<span>PV-Check</span>
						<img src="~/assets/img/icons/tabler-icon-menu-item.svg" alt="" />
					</RouterLink>

					<RouterLink
						v-if="envMetadata?.testing"
						:to="checkLink('sanierungs-check')"
						class="flex justify-between border-b border-b-[--schwarz-15] px-4 py-4 font-bold uppercase"
					>
						<span>Sanierungs-Check</span>
						<img src="~/assets/img/icons/tabler-icon-menu-item.svg" alt="" />
					</RouterLink>

					<RouterLink
						:to="{ name: 'erlaeuterungen' }"
						class="flex justify-between border-b border-b-[--schwarz-15] px-4 py-4 font-bold uppercase"
					>
						<span>Erläuterungen</span>
						<img src="~/assets/img/icons/tabler-icon-menu-item.svg" alt="" />
					</RouterLink>

					<RouterLink
						:to="{ name: 'contact' }"
						class="flex justify-between border-b border-b-[--schwarz-15] px-4 py-4 font-bold uppercase"
					>
						<span>Kontakt</span>
						<img src="~/assets/img/icons/tabler-icon-menu-item.svg" alt="" />
					</RouterLink>
				</nav>
			</div>
		</div>
	</header>

	<div role="navigation" class="hidden justify-between gap-4 px-4 py-2 md:flex">
		<ul class="flex gap-4 py-2">
			<li>
				<RouterLink
					v-slot="{ isActive }"
					:to="{ name: 'index' }"
					class="flex justify-between px-4 py-1 text-lg"
				>
					<span :class="isActive ? 'text-[--rot]' : ''">STARTSEITE</span>
				</RouterLink>
			</li>
			<li>
				<RouterLink
					v-slot="{ isActive }"
					:to="checkLink('object')"
					class="flex justify-between px-4 py-1 text-lg"
				>
					<span :class="isObjectViewPath(isActive) ? 'text-[--rot]' : ''">HEIZUNGS-CHECK</span>
				</RouterLink>
			</li>

			<li v-if="!envMetadata?.testing">
				<RouterLink
					:to="checkLink('pv-check')"
					v-slot="{ isActive }"
					class="flex justify-between px-4 py-1 text-lg"
				>
					<span :class="route.name === 'pv-check' ? 'text-[--rot]' : ''"> PV-CHECK </span>
				</RouterLink>
			</li>
			<li v-else ref="checksMenuRef" class="relative">
				<button class="flex justify-between px-4 py-1 text-lg" @click.stop="toggleChecksMenu">
					<span :class="isChecksActive() ? 'text-[--rot]' : ''"> MEHR CHECKS </span>
					<img
						src="~/assets/img/icons/tabler-icon-menu-item.svg"
						alt=""
						class="ml-2 mt-[2px] h-5 w-5 transition-transform"
						:class="{ 'rotate-90': isChecksMenuOpen }"
					/>
				</button>
				<div
					v-if="isChecksMenuOpen"
					class="absolute left-0 top-full z-50 mt-2 min-w-[230px] border border-[--schwarz-15] bg-white shadow-md"
				>
					<RouterLink
						:to="checkLink('pv-check')"
						class="block border-b border-[--schwarz-15] px-4 py-3 text-base hover:bg-[--schwarz-15]"
					>
						PV-Check
					</RouterLink>
					<RouterLink
						:to="checkLink('sanierungs-check')"
						class="block px-4 py-3 text-base hover:bg-[--schwarz-15]"
					>
						Sanierungs-Check
					</RouterLink>
				</div>
			</li>
		</ul>
		<div class="flex gap-4 py-2 uppercase">
			<RouterLink
				v-slot="{ isActive }"
				:to="{ name: 'erlaeuterungen' }"
				class="flex justify-between px-4 py-1 text-lg"
			>
				<span :class="isActive ? 'text-[--rot]' : ''">ERLÄUTERUNGEN</span>
			</RouterLink>
			<RouterLink v-slot="{ isActive }" :to="{ name: 'contact' }" class="flex justify-between px-4 py-1 text-lg">
				<span :class="isActive ? 'text-[--rot]' : ''">KONTAKT</span>
			</RouterLink>
		</div>
	</div>
</template>
